// GENERAL
import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";

// HOOKS
import { useHostName } from "../../../../../hooks";
import { useUrl } from "../../../../../hooks";
import {
	slideOutMenuOverlayVariant,
	slideOutMenuVariants,
} from "../../../../../motionVariants";
import usePrivilegeCheck from "../../../../../hooks/usePrivilegeCheck";
import { useDataTableContext } from "../../../../../components/ui/Tables/hooks";

// COMPONENTS
import {
	Container,
	Section,
	Toaster,
	LoadingMessage,
	SupplyPortal,
	BaseButton,
	BaseContainer,
	BaseText,
	EventLog,
	BaseModal,
	AddNotesForm,
} from "../../../../../components";
import { FulfillmentModal } from "../..";
import {
	OrderItemsTableSection,
	OrderInvoicesSection,
	OrderDetailsContentSection,
	OrderShipmentsSection,
	OrderShipmentMethodDetails,
	OrderDetailsModalHeader,
} from "./index";

// UTILS
import { getFontAwesomeIcon } from "../../../../../utils";
import formatDateForDetailsBox from "../../../../../utils/formatDateForDetailsBox";

// TYPES
import { OrderViewProps } from "../types";
import { AdminSalesOrderNote } from "../../../types";

// MOTION VARIANTS
const modalVariants = {
	hidden: { opacity: 0 },
	visible: { opacity: 1 },
	exit: { opacity: 0 },
};

const OrderViewModalContent: React.FC<OrderViewProps> = ({
	modalRef,
	borderColor = "primary",
	hasBorder = false,
	handleClose,
	orderDetails,
	isLoading,
	setRefetchData,
	fields,
	salesOrderNotes,
	invoiceData,
	shipmentsData,
	salesOrderItems,
}) => {
	const { isAdmin } = usePrivilegeCheck();
	const { activeUuid, modalTableOpened, setActiveUuid } =
		useDataTableContext();
	const location = useLocation();
	const [openProducts, setOpenProducts] = useState<string[]>([]);
	const prevURL: string = location.state?.prevURL;
	const prevUUID: string = location.state?.prevUUID;
	const { setPreviousUrl, setPreviousItemUuid, previousItemUuid } = useUrl(
		prevURL,
		"",
	);
	const [modalSlug, setModalSlug] = useState<string | null>(null);
	const [modalUuid, setModalUuid] = useState<string | null>(null);

	const { uuid } = useParams<{ uuid: string }>();
	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

	const [isEventLogOpen, setIsEventLogOpen] = useState<boolean>(false);
	const [isViewAllNotesOpen, setIsViewAllNotesOpen] =
		useState<boolean>(false);

	const toggleViewAllNotes = () => {
		setIsViewAllNotesOpen(!isViewAllNotesOpen);
	};

	const toggleMenu = (e?: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
		setIsEventLogOpen(!isEventLogOpen);
	};
	useEffect(() => {
		if (setPreviousUrl) {
			setPreviousUrl(prevURL);
		}
	}, [setPreviousUrl, prevURL]);

	useEffect(() => {
		if (setPreviousItemUuid) {
			setPreviousItemUuid(uuid || "");
		}
	}, [setPreviousItemUuid, prevUUID]);

	const hostName = useHostName().hostName;
	const toggleModal = () => {
		// setIsModalOpen(!isModalOpen);
	};

	///// DATA INTO VARS /////
	const dateOrder = formatDateForDetailsBox(orderDetails?.dateOrder);
	const orderNumber = orderDetails?.number || "—";
	const orderStatus = orderDetails?._status || "—";
	const assignedTo = orderDetails?.approvalUser?._name || "";
	const submittedBy = orderDetails?.createdByUser?._name || "—";
	const priority = orderDetails?.urgency?.name;
	const notifications = orderDetails?.salesOrderEmailAddresses || [];

	const reasonForRequestAndShippingInstructionsNotes = Boolean(
		salesOrderNotes?.reasonForRequest ||
		salesOrderNotes?.shippingInstructions,
	);

	///// TEXT FROM FIELDS /////
	const eventLogTitle = fields?.labels?.eventLogTitle;
	const currencyCharacter =
		orderDetails?.currency?.symbol ||
		fields?.settings?.currencyCharacter ||
		"";
	///// SETTINGS FROM DATA /////
	const shouldRenderDetails =
		submittedBy !== "—" || priority !== "—" || notifications.length > 0;

	if (isLoading)
		return (
			<Container
				containerClasses="container mx-auto px-4"
				additionalClasses="flex justify-center items-center"
			>
				<Section
					additionalClasses="min-full min-h-dvh
				 flex justify-center items-center"
				>
					<LoadingMessage message={fields?.labels?.loadingMessage} />
				</Section>
			</Container>
		);

	if (!orderDetails)
		return (
			<Container>
				<Toaster
					color="red-500"
					message={fields?.labels?.errorLoadingOrderDetails}
					additionalClasses="mt-20 w-1/2 mx-auto text-center"
				/>
			</Container>
		);

	// This can be triggered from a table click or header
	const openFulfillmentModal = (slug: string, uuid: string) => {
		setModalSlug(slug);
		setActiveUuid(uuid);
		setModalUuid(uuid);
		setIsModalOpen(true);
	};

	const fulfillmentModal = modalSlug ? (
		<FulfillmentModal
			key={modalSlug}
			isModalOpen={isModalOpen}
			slug={modalSlug}
			uuid={modalUuid}
		/>
	) : null;

	// CLASSES:
	const overflowClass = isEventLogOpen
		? "overflow-hidden"
		: "overflow-scroll min-h-[95dvh]";
	const modalBorderClassNames = `${overflowClass} bg-white min-w-wideModal rounded-3xl border-2 pt-8 pl-10 pr-5 mb-6 ${
		hasBorder ? `border-${borderColor}` : "border-transparent"
	}`;

	// SLIDE OUT MENU CLASSES:
	const slideOutMenuClassNames = `slide-out-menu bg-white w-1/2 min-w-[300px] max-w-[500px] absolute top-0 left-0 z-40 z-[9999]`;
	const overlayClassNames = `bg-black absolute z-1 w-full left-0 top-0 cursor-pointer z-[9989]`;
	const overlayHeight = "100%";
	const menuItemClassNames = "sticky min-h-full max-h-full mb-20";

	const invoiceSection = Array.isArray(invoiceData)
		? invoiceData.length !== 0 && (
				<OrderInvoicesSection
					invoiceData={invoiceData}
					reasonForRequestAndShippingInstructionsNotes={
						reasonForRequestAndShippingInstructionsNotes
					}
					hostName={hostName}
					fields={fields}
					currencyCharacter={currencyCharacter}
				/>
			)
		: null;

	const shipmentSection =
		Array.isArray(shipmentsData) && shipmentsData.length !== 0 ? (
			<OrderShipmentsSection
				shipmentData={shipmentsData}
				reasonForRequestAndShippingInstructionsNotes={
					reasonForRequestAndShippingInstructionsNotes
				}
				fields={fields}
			/>
		) : null;

	/////////////////////////////////////////////////////////////////////// TEMPORARY TRACKING NUMBERS FOR COMPASS UNTIL BACKEND IS FIXED //////////////////////////////////////////////////////////
	const temporaryTrackingNumbersForCompass = orderDetails?._trackingNumbers
		?.split(",")
		.map((trackingNumberString: string) => trackingNumberString.trim())
		.filter((trackingNumber: string) => trackingNumber.length > 0);

	const trackingNumbers =
		temporaryTrackingNumbersForCompass &&
		(hostName === "COMPASS" || hostName === "COMPASSCANADA") ? (
			<OrderShipmentsSection
				shipmentData={temporaryTrackingNumbersForCompass}
				reasonForRequestAndShippingInstructionsNotes={
					reasonForRequestAndShippingInstructionsNotes
				}
				fields={fields}
			/>
		) : null;

	/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

	const orderShipmentDetailsSection = shouldRenderDetails ? (
		<OrderShipmentMethodDetails
			notifications={notifications}
			submittedBy={submittedBy}
			priority={priority || "—"}
			fields={fields}
		/>
	) : null;

	const salesOrderNotesSection = (
		<div className="flex flex-col md:flex-row items-start gap-2">
			<BaseContainer
				columnContainerClasses={
					"text-sm flex flex-col py-3 px-4 overflow-auto text-left border-transparent w-full"
				}
				columnShape="semi-rounded"
				borderColor="navy-200"
				backgroundColor="bg-navy-100"
				isDisabled={false}
				children={
					<>
						<div className="font-omnes-medium">
							{fields?.labels?.reasonForRequest}
						</div>

						<div>{salesOrderNotes?.reasonForRequest || "-"}</div>
					</>
				}
			/>
			<BaseContainer
				columnContainerClasses={
					"text-sm flex flex-col py-3 px-4 overflow-auto text-left border-transparent w-full"
				}
				columnShape="semi-rounded"
				borderColor="navy-200"
				backgroundColor="bg-navy-100"
				isDisabled={false}
				children={
					<>
						<div className="font-omnes-medium">
							{fields?.labels?.specialInstructions}
						</div>

						<div>
							{salesOrderNotes?.shippingInstructions || "-"}
						</div>
					</>
				}
			/>
		</div>
	);

	const orderAdminNotesSection = isAdmin && activeUuid && (
		<div className="w-full gap-2 flex flex-col">
			<BaseContainer
				columnShape="semi-rounded"
				borderColor="navy-200"
				backgroundColor="bg-navy-100"
				isDisabled={false}
				columnContainerClasses=" text-sm flex flex-col py-3 px-4 text-left border-transparent"
			>
				<AddNotesForm
					notesData={
						salesOrderNotes?.adminNotes as AdminSalesOrderNote[]
					}
					openViewAllNotes={toggleViewAllNotes}
					showViewAllButton={true}
					title={
						<BaseText
							text={fields?.labels?.notesTitle}
							fontFamily="font-sans" // Replace with a valid font family
							tag="h1"
							size="text-sm"
							color="text-navy-500"
							additionalClasses=""
						/>
					}
					showBottomNoteCount={true}
					notesPlacementBottom={true}
					refetchData={() =>
						setRefetchData &&
						setRefetchData((prevState) => !prevState)
					}
					notesListMaxHeight="max-h-[368px] overflow-scroll"
					salesOrderUuid={activeUuid}
				/>
			</BaseContainer>
		</div>
	);

	const orderDetailSections = [
		invoiceSection,
		shipmentSection,
		trackingNumbers,
		orderShipmentDetailsSection,
		salesOrderNotesSection,
		orderAdminNotesSection,
	];

	return (
		orderDetails && (
			<>
				<div
					ref={modalRef}
					className={modalBorderClassNames}
					onClick={(e) => e.stopPropagation()}
				>
					<OrderDetailsModalHeader
						dateOrder={dateOrder}
						orderNumber={orderNumber}
						orderStatus={orderStatus}
						handleClose={handleClose || (() => {})}
						toggleMenu={toggleMenu}
						fields={fields}
					/>
					<OrderDetailsContentSection
						orderDetails={orderDetails}
						fields={fields}
						hostName={hostName}
						currencyCharacter={currencyCharacter}
					/>
					<OrderItemsTableSection
						data={salesOrderItems}
						isLoading={isLoading as boolean}
						toggleModal={toggleModal}
						dynamicModalChange={openFulfillmentModal}
						currencyCharacter={currencyCharacter}
						fields={fields}
					/>
					<section className="grid mt-6 gap-4 grid-cols-1 xl:grid-cols-2">
						{orderDetailSections.map(
							(section, idx) =>
								section && (
									<div key={idx} className="col-span-1">
										{section}
									</div>
								),
						)}
					</section>
				</div>
				{modalTableOpened && fulfillmentModal}
				{isViewAllNotesOpen && activeUuid && (
					<SupplyPortal containerId="portal-root">
						<BaseModal
							containerClassNames="overflow-auto"
							header={<></>}
							isOpen={true}
							onClose={toggleViewAllNotes}
							children={
								<motion.div
									className="fixed inset-0 z-50 flex flex-col items-center justify-center"
									variants={modalVariants}
									initial="hidden"
									animate="visible"
								>
									<BaseContainer
										columnContainerClasses="flex  w-[1020px] h-[800px] flex-col items-start bg-white p-5 rounded-xl border border-gray-200"
										columnShape="semi-rounded"
										borderColor="navy-200"
										isDisabled={false}
									>
										<div className="flex w-full items-center justify-between">
											<BaseText
												text={`Notes (${salesOrderNotes?.adminNotes.length})`}
												fontFamily="font-omnes-medium"
												tag="h1"
												size="text-lg"
												color="text-navy-500"
												additionalClasses=""
											/>
											<BaseButton
												bgColor="bg-white"
												additionalClasses=""
												iconAriaLabel="Close"
												icon={getFontAwesomeIcon(
													"Xmark",
													"regular",
												)}
												as="button"
												onClick={() =>
													toggleViewAllNotes()
												}
												iconClasses="flex items-center text-lg pr-0 size-3 text-navy-300 hover:text-navy-500"
											/>
										</div>

										<AddNotesForm
											notesData={
												salesOrderNotes?.adminNotes as AdminSalesOrderNote[]
											}
											openViewAllNotes={
												toggleViewAllNotes
											}
											showBottomNoteCount={false}
											refetchData={() =>
												setRefetchData &&
												setRefetchData(
													(prevState) => !prevState,
												)
											}
											notesListMaxHeight="h-[600px] overflow-scroll"
											salesOrderUuid={activeUuid}
											addNoteButtonStyles={"bg-gray-100"}
										/>
									</BaseContainer>
								</motion.div>
							}
						/>
					</SupplyPortal>
				)}
				<>
					<AnimatePresence>
						{isEventLogOpen && (
							<motion.div
								initial={slideOutMenuVariants.initial}
								animate={slideOutMenuVariants.animate}
								exit={slideOutMenuVariants.exit}
								transition={slideOutMenuVariants.transition}
								className={slideOutMenuClassNames}
								style={{
									height: overlayHeight,
								}}
							>
								<>
									<div className={menuItemClassNames}>
										<EventLog
											title={eventLogTitle}
											isOpen={isEventLogOpen}
											parentData={orderDetails}
											slug={"sales-orders"}
											hasActivityToggle={true}
										/>
									</div>
								</>
							</motion.div>
						)}
					</AnimatePresence>
					<AnimatePresence>
						{isEventLogOpen && (
							<motion.div
								initial={slideOutMenuOverlayVariant.initial}
								animate={slideOutMenuOverlayVariant.animate}
								exit={slideOutMenuOverlayVariant.exit}
								transition={
									slideOutMenuOverlayVariant.transition
								}
								onClick={toggleMenu}
								className={overlayClassNames}
								style={{
									height: overlayHeight,
								}}
							></motion.div>
						)}
					</AnimatePresence>
				</>
			</>
		)
	);
};

export default OrderViewModalContent;
